import { atom } from 'jotai';
import { Project, ProjectSearchParams, CreateProjectDto, UpdateProjectDto } from '../../types/project';
import { Task } from '../../types/task';
import { ProjectService } from '../../services/projectService';
import { backgroundRefreshingAtom, lastDataRefreshAtom, useCacheFirstAtom } from './uiAtoms';
import { translationEnabledAtom } from './translationAtoms';
import i18n from '../../lib/i18n';
import { fetchTasksAtom, tasksAtom } from './taskAtoms';

// Re-export backgroundRefreshingAtom so other modules can import it from projectAtoms.ts
export { backgroundRefreshingAtom };

// 项目列表状态
export const projectsAtom = atom<Project[]>([]);

// 项目加载状态
export const projectsLoadingAtom = atom<boolean>(false);

// 项目加载错误状态
export const projectsErrorAtom = atom<string | null>(null);

// 项目搜索查询状态
export const projectSearchQueryAtom = atom<string>('');

// 当前选中的项目
export const selectedProjectIdAtom = atom<string | null>(null);

// 通过ID获取项目
export const projectByIdAtom = atom(
  (get) => (id: string | null) => {
    if (!id) return null;
    const projects = get(projectsAtom);
    return projects.find(project => project.id === id) || null;
  }
);

// 加载项目列表的函数
export const fetchProjectsAtom = atom(
  null,
  async (get, set) => {
    // 获取相关状态
    const useCacheFirst = get(useCacheFirstAtom);
    const lastRefreshTimes = get(lastDataRefreshAtom);
    const hasCachedData = get(projectsAtom).length > 0;

    // 如果使用缓存优先且有缓存数据，则使用后台刷新模式
    const useBackgroundRefresh = useCacheFirst && hasCachedData;

    if (useBackgroundRefresh) {
      // 使用后台刷新模式
      set(backgroundRefreshingAtom, true);
      try {
        const projectData = await ProjectService.getProjects();
        set(projectsAtom, projectData);
        set(lastDataRefreshAtom, { ...lastRefreshTimes, projects: Date.now() });
        // Trigger task fetch after successful project fetch in background
        console.log('[fetchProjectsAtom] Background refresh successful, triggering task fetch...');
        set(fetchTasksAtom);
      } catch (err) {
        console.error('Failed to fetch projects:', err);
        set(projectsErrorAtom, err instanceof Error ? err.message : '未知错误');
      } finally {
        set(backgroundRefreshingAtom, false);
      }
    } else {
      // 使用普通加载模式（显示全屏加载状态）
      set(projectsLoadingAtom, true);
      set(projectsErrorAtom, null);
      try {
        const projectData = await ProjectService.getProjects();
        set(projectsAtom, projectData);
        set(lastDataRefreshAtom, { ...lastRefreshTimes, projects: Date.now() });
        // Trigger task fetch after successful project fetch
        console.log('[fetchProjectsAtom] Initial fetch successful, triggering task fetch...');
        set(fetchTasksAtom);
      } catch (err) {
        console.error('Failed to fetch projects:', err);
        set(projectsErrorAtom, err instanceof Error ? err.message : '未知错误');
      } finally {
        set(projectsLoadingAtom, false);
      }
    }
  }
);

// 搜索项目的函数
export const searchProjectsAtom = atom(
  null,
  async (get, set, params: ProjectSearchParams) => {
    try {
      set(projectsLoadingAtom, true);
      set(projectsErrorAtom, null);
      const data = await ProjectService.searchProjects(params);
      set(projectsAtom, data);
    } catch (err) {
      console.error('Failed to search projects:', err);
      set(projectsErrorAtom, err instanceof Error ? err.message : '未知错误');
    } finally {
      set(projectsLoadingAtom, false);
    }
  }
);

// 创建项目的函数
export const createProjectAtom = atom(
  null,
  async (get, set, projectData: CreateProjectDto) => {
    // --- Translation Check ---
    if (get(translationEnabledAtom)) {
      alert(i18n.t('v0/common:errors.editingDisabledWhileTranslating'));
      return null;
    }
    // --- End Translation Check ---

    set(projectsLoadingAtom, true);
    try {
      await ProjectService.createProject(projectData);
      // 重新获取项目列表
      const data = await ProjectService.getProjects();
      set(projectsAtom, data);
      return true;
    } catch (err) {
      console.error('Failed to create project:', err);
      set(projectsErrorAtom, err instanceof Error ? err.message : '未知错误');
      return false;
    } finally {
      set(projectsLoadingAtom, false);
    }
  }
);

// 更新项目的函数
export const updateProjectAtom = atom(
  null,
  async (get, set, projectData: UpdateProjectDto) => {
    // --- Translation Check ---
    if (get(translationEnabledAtom)) {
      alert(i18n.t('v0/common:errors.editingDisabledWhileTranslating'));
      return null;
    }
    // --- End Translation Check ---

    set(projectsLoadingAtom, true);
    try {
      await ProjectService.updateProject(projectData);
      // 重新获取项目列表
      const data = await ProjectService.getProjects();
      set(projectsAtom, data);
      return true;
    } catch (err) {
      console.error('Failed to update project:', err);
      set(projectsErrorAtom, err instanceof Error ? err.message : '未知错误');
      return false;
    } finally {
      set(projectsLoadingAtom, false);
    }
  }
);

// 删除项目的函数
export const deleteProjectAtom = atom(
  null,
  async (get, set, id: string, cascadeDelete: boolean = false) => {
    // --- Translation Check ---
    if (get(translationEnabledAtom)) {
      alert(i18n.t('v0/common:errors.editingDisabledWhileTranslating'));
      return null;
    }
    // --- End Translation Check ---

    set(projectsLoadingAtom, true);
    try {
      await ProjectService.deleteProject(id, cascadeDelete);
      // 更新本地状态，无需重新获取
      const projects = get(projectsAtom);
      set(projectsAtom, projects.filter(project => project.id !== id));

      // 如果级联删除，也更新任务状态
      if (cascadeDelete) {
        const tasks = get(tasksAtom);
        const updatedTasks = tasks.filter(task => task.projectId !== id);
        set(tasksAtom, updatedTasks);
      }

      return true;
    } catch (err) {
      console.error('Failed to delete project:', err);
      set(projectsErrorAtom, err instanceof Error ? err.message : '未知错误');
      return false;
    } finally {
      set(projectsLoadingAtom, false);
    }
  }
);

// 由项目ID加载的项目任务
export const projectTasksAtom = atom<Record<string, Task[]>>({});

// 强制刷新项目数据（忽略缓存）
export const forceRefreshProjectsAtom = atom(
  null,
  async (get, set) => {
    const lastRefreshTimes = get(lastDataRefreshAtom);

    set(projectsLoadingAtom, true);
    set(projectsErrorAtom, null);
    try {
      const projectData = await ProjectService.getProjects();
      set(projectsAtom, projectData);
      set(lastDataRefreshAtom, { ...lastRefreshTimes, projects: Date.now() });
      // Also trigger task fetch on force refresh
      console.log('[forceRefreshProjectsAtom] Force refresh successful, triggering task fetch...');
      set(fetchTasksAtom);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      set(projectsErrorAtom, err instanceof Error ? err.message : '未知错误');
    } finally {
      set(projectsLoadingAtom, false);
    }
  }
);